import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { finalize, Subscription } from 'rxjs';

import { PageNames } from 'src/app/configs/page-names.enum';
import { ConvertDateToTimePipe } from 'src/app/core/pipes/convert-date-to-time.pipe';
import { DateTimeConvertorPipe } from 'src/app/core/pipes/date-time-convertor.pipe';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { NotificationTypes } from '../../../../../core/api/models/notification-types';
import { DynamicTabService } from '../../../../../core/services/dynamic-tab.service';
import { NotificationsIconDirective } from '../notifications-icon.directive';
import { CreateNotificationSeenCommand } from './../../../../../core/api/models/create-notification-seen-command';
import { NotificationsListDto } from './../../../../../core/api/models/notifications-list-dto';
import { NotificationsService } from './../../../../../core/api/services/notifications.service';

@Component({
  selector: 'notification-item',
  imports: [
    CommonModule,
    TranslateModule,
    MatTooltipModule,
    InlineSVGModule,
    NotificationsIconDirective,
    ConvertDateToTimePipe,
    DateTimeConvertorPipe,
  ],
  templateUrl: './notification-item.component.html',
  styleUrls: ['./notification-item.component.scss'],
})
export class NotificationItemComponent implements OnDestroy {
  //#region props
  appStateService = inject(AppStateService);
  readonly notification = input<NotificationsListDto>();
  notificationsService = inject(NotificationsService);
  dynamicTabService = inject(DynamicTabService);
  readonly updateNotifications = output<any>();
  subscription: Subscription;
  loading = signal(false);
  readonly msgElement =
    viewChild<ElementRef<HTMLParagraphElement>>('msgElement');
  readonly isTextClamped = signal(false);

  constructor() {
    effect(() => {
      this.notification();
      const element = this.msgElement()?.nativeElement;
      if (!element) return;

      requestAnimationFrame(() => {
        this.isTextClamped.set(element.scrollHeight - element.clientHeight > 1);
      });
    });
  }

  get NotificationTypes() {
    return NotificationTypes;
  }
  get pageNames() {
    return PageNames;
  }
  //#endregion

  notificationSeenApi(notificationId?) {
    if (notificationId == 0) return;
    if (this.subscription) this.subscription.unsubscribe();

    this.loading.set(true);

    const body: CreateNotificationSeenCommand = {};
    if (notificationId) body.notificationId = notificationId;

    this.subscription = this.notificationsService
      .notificationsCreateNotificationSeen({ body })
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
      )
      .subscribe((res) => {
        if (res.isSucceed) this.updateNotifications.emit(res);
      });
  }

  readonly expanded = signal(false);

  toggleExpanded() {
    this.expanded.update((value) => !value);
  }

  openTab(tabName) {
    this.dynamicTabService.setNewTab('');
    this.dynamicTabService.setNewTab(tabName);
    this.dynamicTabService.setActiveTab(tabName);
  }

  ngOnDestroy() {
    if (this.subscription) this.subscription.unsubscribe();
  }
}
